import { useMemo, useState } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { useFlags } from '@mint-lib/routing-context';
import {
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogRoot,
  DialogTitle,
  Tabs,
  TabsList,
  TabsTrigger,
  ToggleGroup,
  ToggleGroupItem,
  typographyVariants,
} from '@mint-lib/tw-components';

import { Label, UserType } from '../../api/users/index.js';

type Props = {
  className?: string;
  onClose: () => void;
  userProfile: Partial<UserType>;
  isActive: boolean;
  tabs: { id: string; name: string }[];
};

const LabelModal: React.FC<Props> = ({
  onClose,
  isActive,
  userProfile,
  tabs,
}) => {
  const { t } = useTranslation('@myn/permissions');
  const ff = useFlags('@mint/arm', ['brandsAndGeoToMdm']);

  const [categoryType, setCategoryType] = useState<string>(
    ff.brandsAndGeoToMdm.enabled
      ? (userProfile.tags?.[0]?.categoryType ?? 'brands')
      : 'brands',
  );

  const filterLabels = useMemo(
    () =>
      userProfile.tags
        ?.filter((tag: Label) => tag.categoryType === categoryType)
        .sort((a: Label, b: Label) => +a.level - +b.level),
    [userProfile.tags, categoryType],
  );
  const subCategories = useMemo(
    () =>
      [...new Set(filterLabels?.map((tag: Label) => tag.category))].map(
        (subCategory: string | undefined) => {
          return {
            name: subCategory ?? '',
            labels: filterLabels?.filter(
              (label: Label) => label.category === subCategory,
            ),
          };
        },
      ),
    [filterLabels],
  );

  return (
    <Dialog
      open={isActive}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <DialogPortal>
        <DialogOverlay />
        <DialogRoot size="m">
          <DialogHeader onClose={onClose}>
            <DialogTitle>
              {`${userProfile.firstName} ${userProfile.lastName} ` +
                t('Assigned Labels')}
            </DialogTitle>
          </DialogHeader>
          <DialogContent className="flex flex-col gap-4">
            {ff.brandsAndGeoToMdm.enabled ? (
              <Tabs
                value={categoryType}
                onValueChange={setCategoryType}
                orientation="horizontal"
              >
                {/* Long hierarchy names stay on one line; the row scrolls sideways. */}
                <TabsList className="max-w-full overflow-x-auto overflow-y-hidden">
                  {tabs.map((tab) => (
                    <TabsTrigger
                      value={tab.name}
                      key={tab.id}
                      className="whitespace-nowrap"
                    >
                      {tab.name}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>
            ) : (
              <ToggleGroup
                type="single"
                value={categoryType}
                size="large"
                onValueChange={(value) => {
                  if (value) {
                    setCategoryType(value);
                  }
                }}
              >
                <ToggleGroupItem value="brands">
                  {t('Brands & products')}
                </ToggleGroupItem>
                <ToggleGroupItem value="geographies">
                  {t('Geographies')}
                </ToggleGroupItem>
              </ToggleGroup>
            )}

            {subCategories.length === 0 ? (
              <div className="flex justify-center py-4">
                <p className={typographyVariants({ variant: 'h4' })}>
                  {t(`There are no labels assigned yet`)}
                </p>
              </div>
            ) : (
              subCategories.map((subCategory) => {
                return (
                  <div className="flex flex-col gap-2" key={subCategory.name}>
                    <p className={typographyVariants({ variant: 'h6' })}>
                      {subCategory.name}
                    </p>
                    <div className="flex flex-row flex-wrap gap-2">
                      {subCategory.labels?.map((label) => {
                        return (
                          <Chip
                            key={label.name}
                            variant="outline"
                            color="silver"
                            size="medium"
                          >
                            {label.name}
                          </Chip>
                        );
                      })}
                    </div>
                  </div>
                );
              })
            )}
          </DialogContent>
          <DialogFooter>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={onClose}
              aria-label="close labels"
            >
              {t('Close')}
            </Button>
          </DialogFooter>
        </DialogRoot>
      </DialogPortal>
    </Dialog>
  );
};

export default LabelModal;
